import Dexie from "dexie"
export const db = new Dexie("vslaDBv2")
import CryptoJS from "crypto-js"
import { v4 as uuidv4 } from "uuid"

db.version(2).stores({
  users: "userId, encryptedPin, salt",
  cacheStore: "url, data, timestamp",
  groups: "cacheId, vslaId, encryptedData, isSynced, error, requestQueueId, userId",
  offlineRequests: "&cacheId, tempId, dependsOn, status, entityType, encryptedBody, salt, userId, [userId+status]",
  depsMap: "&[entityType+userId], cacheId, value, key, userId"
});

const checkStorageSize = async (sizeInBytes) => {
  if (!navigator.storage || !navigator.storage.estimate) {
    // Fallback to checking the total space available in the origin's storage
    const { usage, quota } = await navigator.storageQuota.queryInfo("temporary")
    return quota - usage >= sizeInBytes
  }
  const { usage, quota } = await navigator.storage.estimate()
  return usage + sizeInBytes <= quota
}

export const getCacheByKey = async (table, key, value) => {
  if (!value) {
    return Promise.reject(new Error(`Invalid, ${key}: ${value}`))
  }
  try {
    return await db.table(table).get({ [key]: value })
  } catch (error) {
    console.log(`error getting cache by ${key}: ${value}`)
    Promise.reject()
  }
}

//Encryption and decryption functions.
export function encryptObject(data, salt) {
  return CryptoJS.AES.encrypt(data, salt).toString()
}

export function decryptObject(data, salt) {
  try {
    if (!data || !salt) {
      throw new Error("invalid decryption credentials", data, salt)
    }
    const bytes = CryptoJS.AES.decrypt(data, salt)
    return bytes.toString(CryptoJS.enc.Utf8)
  } catch (e) {
    console.error("Error: ", e)
    return null
  }
}

export async function loginOfflineUser(data) {
  const salt = CryptoJS.lib.WordArray.random(128 / 8).toString()
  const encrypted = encryptObject(JSON.stringify(data), salt)

  // this will create a new entry if it does not exist

  await updateCacheItem(
    "cacheStore",
    {
      url: data.url,
      data: { encrypted, salt },
      timestamp: Date.now(),
    },
    "url",
    data.url,
  )
}

export async function registerUser(userId, pin) {
  const user = await getCacheByKey("users", "userId", userId)
  if (user) {
    return
  } // user already registered - skip
  const salt = CryptoJS.lib.WordArray.random(128 / 8).toString()
  const encryptedPin = encryptObject(pin, salt)
  // this will create a new entry if it does not exist
  await updateCacheItem("users", { userId, encryptedPin, salt }, "userId", userId)
}

const checkPersistStorage = async () => {
  if (!navigator.storage || !navigator.storage.persist) {
    return false
  }
  //check whether the storage is currently persisted or not
  const persisted = await navigator.storage.persisted()
  if (persisted) {
    return true
  }
  //requests the browser to persist storage
  const granted = await navigator.storage.persist()
  return granted
}

export const isIndexedDBSupported = () => {
  return "indexedDB" in window
}

export const addToCache = async (table, data, currentUserId, primaryKey = null) => {
  try {
    // 1. Prepare the data to be added, ensuring the userId is included.
    const dataToCache = { ...data, userId: currentUserId };

    // 2. Handle primary keys based on the table.
    let newCacheId;
    switch (table) {
      case 'depsMap':
        // The primary key for 'depsMap' is a compound key: [entityType, userId].
        // The call to this function MUST provide the primaryKey array.
        if (!Array.isArray(primaryKey) || primaryKey.length !== 2) {
          throw new Error('For depsMap table, a valid compound primaryKey array [entityType, userId] must be provided.');
        }
        await db.table(table).add(dataToCache, primaryKey);
        newCacheId = primaryKey.join('-'); // A simple string representation for returning.
        break;

      case 'groups':
      case 'offlineRequests':
        // These tables use a single-column primary key, which is `cacheId`.
        dataToCache.cacheId = primaryKey || uuidv4();
        await db.table(table).add(dataToCache);
        newCacheId = dataToCache.cacheId;
        break;

      default:
        // For all other tables, assume no specific primary key handling is needed.
        // Dexie will use an auto-incrementing key by default if none is provided.
        newCacheId = await db.table(table).add(dataToCache);
        break;
    }

    console.log(`Successfully added cache to table: ${table}. ID: ${newCacheId}`);
    return newCacheId;
  } catch (error) {
    console.error(`Failed to add cache for table: ${table}.`, error);
    // Re-throw the error to ensure the calling function can handle it.
    throw error;
  }
};

export const deleteCacheByKey = async (table, key, value) => {
  if (!value || !key) {
    console.log(`Error: invalid, ${key}: ${value}`)
    return
  }
  if (table === "offlineMeeting") {
    localStorage.removeItem("_m_id")
  }

  try {
    const res = await db
      .table(table)
      .where({ [key]: value })
      .delete()
    console.log(`successfully deleted row  ${key}: ${value} of table: ${table}`)
    return res
  } catch (error) {
    console.log(`failed to delete cache ${key}: ${value} for table: ${table}`)
  }
}

export const updateCacheItem = async (table, data, key, value, currentUserId) => {
  try {
    if (!value) {
      await addToCache(table, data, currentUserId)
      return
    }
    await db.table(table).put({ [key]: value, ...data })
    console.info(`successfully updated cache ${key}: ${value} of table: ${table}`)
  } catch (error) {
    console.error(
      `failed to update cache ${key}: ${value} of table: ${table} error: ${error}`,
    )
  }
}


export const patchCacheItem = async (table, primaryKey, data) => {
  if ([table, primaryKey, data].some(a => !a)) {
    throw new Error(`Required params missing. table: ${table}, PK: ${primaryKey}, data: ${JSON.stringify(data)}`,)
  }
  try {
    await db.table(table).update(primaryKey, data)
    console.info(`[${table}]: Cache item updated key ${primaryKey}`)
  } catch (error) {
    console.error(
      `[${table}]: Failed to patch cache primary key ${primaryKey} error: ${error}`,
    )
  }
}

export const getCacheItemByRefID = async (table, refId) => {
  if (!refId) {
    console.log(`Invalid refId: ${refId}`)
    return
  }
  try {
    const result = await db.table(table).where({ refId }).toArray()
    return result
  } catch (error) {
    console.error(`[${table}]: Error getting cache item by refId: ${refId} error: ${error}`)
  }
}

export const getCacheById = async (table, cacheId) => {
  try {
    if (!cacheId) {
      console.log(`Invalid cacheId: ${cacheId}`)
      return
    }
    const result = await db.table(table).where({ cacheId }).toArray()
    return result
  } catch (error) {
    console.error(`[${table}]: Error getting cache item by cacheId: ${cacheId} error: ${error}`)
  }
}

export const clearAllTables = async () => {
  try {
    const tableNames = ["accounts", "members"]
    for (const tableName of tableNames) {
      await db.table(tableName).clear()
      console.log(`Successfully cleared all data from table: ${tableName}`)
    }
    //clear meeting table if there are no transactions and user is logging out
    await clearOfflineMeetingTable()
  } catch (error) {
    console.error(`Failed to clear data from all tables: ${error}`)
  }
}

async function clearOfflineMeetingTable(currentUserId) {
  try {
    const meetingsToDelete = await db
      .table("offlineMeeting")
      .where({ userId: currentUserId })
      .filter((meeting) => {
        return (
          meeting.data.status === "ongoing" && meeting.data.transactions.length === 0
        )
      })
      .toArray()

    for (const meeting of meetingsToDelete) {
      await db.table("offlineMeeting").delete(meeting.cacheId)
      console.log(`Deleted meeting with ID ${meeting.cacheId} for user ${currentUserId}`)
    }
  } catch (error) {
    console.error("Error while clearing offlineMeeting table:", error)
  }
}

export const updateDependencyRecords = async (primaryKey, obj, currentUserId) => {
  if (!primaryKey || (Array.isArray(primaryKey) && primaryKey.length < 2)) {
    throw new Error(`invalid prmimaryKey: ${primaryKey}`)
  }
  else if (!obj) {
    throw new Error(`invalid update object: ${JSON.stringify(primaryKey)}`)
  }
  else if (!currentUserId) {
    throw new Error(`invalid userId: ${currentUserId}`)
  }
  // let requests = await db.offlineRequests.toArray()
  // requests = requests.filter(req=>req.needBodyUpdate && req.needBodyUpdate[obj.key])
  const dependencyTable = 'depsMap'
  const dependency = await db.table(dependencyTable)
    .where('[entityType+userId]').equals(primaryKey).first();
  if (dependency?.value) {
    // skip. Dependency available
  }
  else if (dependency) {
    await patchCacheItem(dependencyTable, primaryKey, obj)
  } else if (!dependency) {
    // we add placeholder dependency map to capture ref UUID
    await addToCache(dependencyTable, obj, currentUserId, primaryKey)
    // to be cleaned up after requests completed
  }
  return Promise.resolve(!!obj.value) // Indicate that the dependecy is avalaible
}

export const resetRequestsErrors = async () => {
  const offlineRequests = await db.offlineRequests.toArray();
  offlineRequests.forEach(async req => { // reset error flags from last attempt
    await db.table('offlineRequests').update(req.cacheId, {
      status: 'pending',
      error: null,
      attempts: 0,
    });
  });
}

export const checkOnlineStatus = async () => {
  try {
    const url = new URL("/config.js", window.location.origin)
    url.searchParams.append("r", new Date().getTime())
    const response = await fetch(url, {
      method: "HEAD",
      cache: "no-store",
    })
    return response.status >= 200 && response.status < 300
  } catch (error) {
    return false
  }
}